import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ENGAGEMENT = ["Online", "Physical"];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // Build YYYY-MM-DDTHH:MM in local time
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function EventFormDialog({ open, onClose, onSubmit, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    title: "",
    date_time: toLocalInput(new Date().toISOString()),
    place: "",
    engagement_type: "Online",
    remarks: "",
    minutes_of_meeting: "",
    next_steps: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {

      // eslint-disable-next-line
      setForm({
        title: initial.title || "",
        date_time: toLocalInput(initial.date_time),
        place: initial.place || "",
        engagement_type: initial.engagement_type || "Online",
        remarks: initial.remarks || "",
        minutes_of_meeting: initial.minutes_of_meeting || "",
        next_steps: initial.next_steps || "",
      });
    } else {
      setForm({
        title: "",
        date_time: toLocalInput(new Date().toISOString()),
        place: "",
        engagement_type: "Online",
        remarks: "",
        minutes_of_meeting: "",
        next_steps: "",
      });
    }
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.date_time) {
      toast.error("Date & time is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Convert local datetime-local string to ISO with timezone
        date_time: new Date(form.date_time).toISOString(),
      };
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="event-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Event" : "Log New Event"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Capture meetings, calls and engagements with this customer.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
            <Input
              data-testid="event-form-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. Pricing discussion, Demo call"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Date & Time
            </Label>
            <Input
              data-testid="event-form-date-time"
              type="datetime-local"
              value={form.date_time}
              onChange={(e) => update("date_time", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Engagement Type
            </Label>
            <Select
              value={form.engagement_type}
              onValueChange={(v) => update("engagement_type", v)}
            >
              <SelectTrigger className="mt-1.5" data-testid="event-form-engagement">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENGAGEMENT.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Place / Link
            </Label>
            <Input
              data-testid="event-form-place"
              value={form.place}
              onChange={(e) => update("place", e.target.value)}
              placeholder="e.g. Mumbai office, Google Meet link"
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Remarks
            </Label>
            <Textarea
              data-testid="event-form-remarks"
              value={form.remarks}
              onChange={(e) => update("remarks", e.target.value)}
              rows={2}
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Minutes of Meeting
            </Label>
            <Textarea
              data-testid="event-form-mom"
              value={form.minutes_of_meeting}
              onChange={(e) => update("minutes_of_meeting", e.target.value)}
              rows={4}
              placeholder="Key discussion points, decisions made…"
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Next Steps
            </Label>
            <Textarea
              data-testid="event-form-next-steps"
              value={form.next_steps}
              onChange={(e) => update("next_steps", e.target.value)}
              rows={3}
              placeholder="Action items, follow-ups, owners…"
              className="mt-1.5"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="event-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            data-testid="event-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Log event"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
